// AGC: CountryLanguageStep - First step in farm setup wizard
// AGC: Detects user's country automatically and sets appropriate language

import React, { useState, useEffect, useRef } from 'react'
import { motion } from 'framer-motion'
import { Globe, ChevronRight, ChevronLeft, Loader2, MapPin } from 'lucide-react'
import { useTranslation } from '../../hooks/useTranslation'
import toast from 'react-hot-toast'

// =============================================================================
// AGC: TYPES
// =============================================================================

interface Country {
  code: string
  name: string
  language: 'pt' | 'es' | 'en'
  flag: string
  centerCoords: [number, number]
}

interface CountryLanguageStepProps {
  onNext: (data: { country: string; language: string; countryData: Country }) => void
  onBack?: () => void
  isFirst?: boolean
}

// =============================================================================
// AGC: SUPPORTED COUNTRIES
// =============================================================================

const SUPPORTED_COUNTRIES: Country[] = [
  { code: 'BR', name: 'Brasil', language: 'pt', flag: '🇧🇷', centerCoords: [-15.7975, -47.8919] },
  { code: 'AR', name: 'Argentina', language: 'es', flag: '🇦🇷', centerCoords: [-34.6037, -58.3816] },
  { code: 'UY', name: 'Uruguay', language: 'es', flag: '🇺🇾', centerCoords: [-34.9011, -56.1645] },
  { code: 'PY', name: 'Paraguay', language: 'es', flag: '🇵🇾', centerCoords: [-25.2637, -57.5759] },
  { code: 'CL', name: 'Chile', language: 'es', flag: '🇨🇱', centerCoords: [-33.4489, -70.6693] },
  { code: 'CO', name: 'Colombia', language: 'es', flag: '🇨🇴', centerCoords: [4.7110, -74.0721] },
  { code: 'PE', name: 'Perú', language: 'es', flag: '🇵🇪', centerCoords: [-12.0464, -77.0428] },
  { code: 'BO', name: 'Bolivia', language: 'es', flag: '🇧🇴', centerCoords: [-19.0196, -65.2619] },
  { code: 'MX', name: 'México', language: 'es', flag: '🇲🇽', centerCoords: [19.4326, -99.1332] },
  { code: 'EC', name: 'Ecuador', language: 'es', flag: '🇪🇨', centerCoords: [-0.1807, -78.4678] },
  { code: 'US', name: 'United States', language: 'en', flag: '🇺🇸', centerCoords: [39.8283, -98.5795] },
  { code: 'OTHER', name: 'Other', language: 'en', flag: '🌍', centerCoords: [0, 0] },
]

// =============================================================================
// AGC: HELPER FUNCTIONS
// =============================================================================

/**
 * AGC: Detect country from GPS coordinates using Nominatim reverse geocoding
 */
const detectCountryFromGPS = async (lat: number, lon: number): Promise<string | null> => {
  try {
    const response = await fetch(
      `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lon}&zoom=3`,
      {
        headers: {
          'User-Agent': 'DroidFarm/1.0'
        }
      }
    )
    
    if (!response.ok) return null
    
    const data = await response.json()
    const countryCode = data.address?.country_code?.toUpperCase()
    
    console.log('AGC: Detected country from GPS:', countryCode)
    return countryCode || null
  } catch (error) {
    console.error('AGC: Error detecting country from GPS:', error)
    return null
  }
}

/**
 * AGC: Detect country from browser language/timezone
 */
const detectCountryFromBrowser = (): string | null => {
  // AGC: Try timezone detection first (more reliable for country)
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone
  const tzCountryMap: Record<string, string> = {
    'Sao_Paulo': 'BR', 'Brazil': 'BR', 'Fortaleza': 'BR', 'Manaus': 'BR', 'Recife': 'BR',
    'Buenos_Aires': 'AR', 'Cordoba': 'AR', 'Argentina': 'AR',
    'Montevideo': 'UY',
    'Asuncion': 'PY',
    'Santiago': 'CL',
    'Bogota': 'CO',
    'Lima': 'PE',
    'La_Paz': 'BO',
    'Mexico_City': 'MX', 'Cancun': 'MX', 'Tijuana': 'MX',
    'Guayaquil': 'EC', 'Quito': 'EC',
    'New_York': 'US', 'Chicago': 'US', 'Denver': 'US', 'Los_Angeles': 'US',
  }
  for (const [tzKey, code] of Object.entries(tzCountryMap)) {
    if (timezone.includes(tzKey)) return code
  }

  // AGC: Fallback to browser language
  const browserLang = navigator.language.toLowerCase()
  if (browserLang.startsWith('pt')) return 'BR'
  if (browserLang.startsWith('es')) return 'AR'
  if (browserLang.startsWith('en')) return 'US'

  return null
}

/**
 * AGC: Get current GPS position
 * AGC: Uses nativeGeolocation service (Capacitor on native, browser API on web)
 */
const getCurrentPosition = async (): Promise<{ coords: { latitude: number; longitude: number } }> => {
  const { getCurrentPosition: getNativePosition } = await import('../../services/nativeGeolocation')
  const coords = await getNativePosition({ enableHighAccuracy: false, timeout: 5000, maximumAge: 0 })
  // AGC: Return shape compatible with existing callers that destructure .coords
  return { coords }
}

// =============================================================================
// AGC: COMPONENT
// =============================================================================

export const CountryLanguageStep: React.FC<CountryLanguageStepProps> = ({
  onNext,
  onBack,
  isFirst = true,
}) => {
  const { t, changeLanguage } = useTranslation()
  
  const [isDetecting, setIsDetecting] = useState(true)
  const [detectedCountry, setDetectedCountry] = useState<string | null>(null)
  const [selectedCountry, setSelectedCountry] = useState<Country | null>(null)
  const [selectedLanguage, setSelectedLanguage] = useState<'pt' | 'es' | 'en'>('pt')

  // AGC: Guard against re-running detection when deps change (e.g. `t` ref after changeLanguage).
  // AGC: Re-running would overwrite the user's manual country pick with the GPS-detected country.
  const hasDetectedRef = useRef(false)

  // AGC: Auto-detect country on mount
  useEffect(() => {
    if (hasDetectedRef.current) return
    hasDetectedRef.current = true
    const detectCountry = async () => {
      setIsDetecting(true)
      
      try {
        // AGC: Try GPS detection first
        const position = await getCurrentPosition()
        const { latitude, longitude } = position.coords
        
        console.log('AGC: Got GPS position:', latitude, longitude)
        
        const countryCode = await detectCountryFromGPS(latitude, longitude)
        
        if (countryCode) {
          setDetectedCountry(countryCode)
          
          // AGC: Auto-select if it's a supported country
          const country = SUPPORTED_COUNTRIES.find(c => c.code === countryCode)
          if (country) {
            setSelectedCountry(country)
            setSelectedLanguage(country.language)
            
            // AGC: Change language immediately
            await changeLanguage(country.language)
            
            toast.success(t('wizard.country.detected') + ` ${country.name}`)
          }
        } else {
          // AGC: Fallback to browser detection
          const browserCountry = detectCountryFromBrowser()
          if (browserCountry) {
            const country = SUPPORTED_COUNTRIES.find(c => c.code === browserCountry)
            if (country) {
              setSelectedCountry(country)
              setSelectedLanguage(country.language)
              await changeLanguage(country.language)
            }
          }
        }
      } catch (error) {
        console.warn('AGC: GPS detection failed, using browser fallback:', error)
        
        // AGC: Fallback to browser detection
        const browserCountry = detectCountryFromBrowser()
        if (browserCountry) {
          const country = SUPPORTED_COUNTRIES.find(c => c.code === browserCountry)
          if (country) {
            setSelectedCountry(country)
            setSelectedLanguage(country.language)
            await changeLanguage(country.language)
          }
        }
      } finally {
        setIsDetecting(false)
      }
    }
    
    detectCountry()
  }, [changeLanguage, t])
  
  const handleCountrySelect = async (country: Country) => {
    setSelectedCountry(country)
    setSelectedLanguage(country.language)
    
    // AGC: Change language immediately
    await changeLanguage(country.language)
  }
  
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    
    if (selectedCountry) {
      onNext({
        country: selectedCountry.code,
        language: selectedLanguage,
        countryData: selectedCountry,
      })
    }
  }
  
  return (
    <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0 px-6">
      {/* AGC: Scrollable content area for keyboard safety */}
      <div className="flex-1 min-h-0 overflow-y-auto pb-2">
        <div className="flex flex-col items-center pt-4">
          {/* AGC: Icon */}
          <div
            className="w-16 h-16 rounded-full flex items-center justify-center mb-4 mx-auto flex-shrink-0"
            style={{ backgroundColor: 'var(--accent-light-bg)' }}
          >
            <Globe className="w-8 h-8" style={{ color: 'var(--accent-primary)' }} />
          </div>

          {/* AGC: Title */}
          <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-2 text-center">
            {t('wizard.country.title')}
          </h2>

          <p className="text-gray-600 dark:text-gray-400 mb-4 text-center text-sm">
            {t('wizard.country.description')}
          </p>
        </div>

        {/* AGC: Loading state */}
        {isDetecting && (
          <div className="flex items-center justify-center gap-3 mb-6">
            <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
            <span className="text-sm text-gray-500 dark:text-gray-400">
              {t('wizard.location.gettingLocation')}
            </span>
          </div>
        )}

        {/* AGC: Country selection */}
        {!isDetecting && (
          <div className="space-y-2">
            {detectedCountry && selectedCountry && (
              <div className="mb-3 p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
                <div className="flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-green-600 dark:text-green-400" />
                  <p className="text-sm text-green-700 dark:text-green-300">
                    {t('wizard.country.detected')} <strong>{selectedCountry.name}</strong>
                  </p>
                </div>
              </div>
            )}

            <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {t('wizard.country.select')}
            </p>

            {SUPPORTED_COUNTRIES.map((country) => (
              <button
                key={country.code}
                type="button"
                onClick={() => handleCountrySelect(country)}
                className={`w-full p-3 rounded-xl border-2 text-left transition-all ${
                  selectedCountry?.code === country.code
                    ? 'border-[var(--accent-primary)] bg-[var(--accent-light-bg)]'
                    : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <span className="text-2xl">{country.flag}</span>
                    <div>
                      <h4 className="font-semibold text-gray-900 dark:text-white text-sm">
                        {country.code === 'OTHER' ? t('wizard.country.otherCountry') : country.name}
                      </h4>
                      <p className="text-xs text-gray-600 dark:text-gray-400">
                        {country.language === 'pt' ? 'Português' : country.language === 'es' ? 'Español' : 'English'}
                      </p>
                    </div>
                  </div>
                  {selectedCountry?.code === country.code && (
                    <div
                      className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0"
                      style={{ backgroundColor: 'var(--accent-primary)' }}
                    >
                      <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                  )}
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* AGC: Actions — pinned at bottom, never hidden by keyboard */}
      <div className="flex gap-3 py-4 flex-shrink-0">
        {!isFirst && onBack && (
          <button
            type="button"
            onClick={onBack}
            className="flex-1 px-4 py-3 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-xl font-medium hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors flex items-center justify-center gap-2"
          >
            <ChevronLeft className="w-5 h-5" />
            {t('common.back')}
          </button>
        )}
        <button
          type="submit"
          disabled={!selectedCountry || isDetecting}
          className="flex-1 px-4 py-3 text-white rounded-xl font-medium hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          style={{ backgroundColor: 'var(--accent-primary)' }}
        >
          {t('wizard.country.confirm')}
          <ChevronRight className="w-5 h-5" />
        </button>
      </div>
    </form>
  )
}

export default CountryLanguageStep
